import React, { useContext, useState, useEffect, useMemo } from 'react';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { Context as AlertContext } from 'contexts/alert';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Config } from 'configs';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import moment from 'moment-timezone';
import { DropDownList } from 'components/formInputs/dropDownList';
import { FormButton } from 'components/buttons/formButton';
import { TextboxWrapper } from 'components/formInputs/wrappers/textboxWrapper';
import { DropdownWrapper } from 'components/formInputs/wrappers/dropdownWrapper';

const restaurantTypes = [
  { value: 'OrderAndPay', label: 'Order and Pay' },
  { value: 'PayOnly', label: 'Only Pay' },
  { value: 'TipsOnly', label: 'Only Tips' },
];

export const defaultLanguageCodes = [
  { value: '', label: '-' },
  { value: 'en', label: 'English' },
  { value: 'lt', label: 'Lithuanian' },
  { value: 'lv', label: 'Latvian' },
  { value: 'ru', label: 'Russian' },
  { value: 'pl', label: 'Polish' },
  { value: 'ee', label: 'Estonian' },
  { value: 'bg', label: 'Bulgarian' },
  { value: 'de', label: 'German' },
  { value: 'dk', label: 'Danish' },
  { value: 'es', label: 'Spanish' },
  { value: 'fi', label: 'Finnish' },
  { value: 'no', label: 'Norwegian' },
  { value: 'ro', label: 'Romanian' },
  { value: 'se', label: 'Swedish' },
];

export const RestaurantsAddEditFormGeneral = ({ itemData, isEdit }) => {
  const navigate = useNavigate();

  const { addNew, editById, getCompanyRestaurants } = useContext(CompanyContext);
  const { state: auth } = useContext(AuthContext);
  const { alertController } = useContext(AlertContext);

  const [loading, setLoading] = useState(false);

  const schema = yup.object().shape({
    _id: yup.string().default(itemData?._id),
    name: yup.string().required('Please enter a restaurnat name.').default(itemData?.name),
    street: yup.string().default(itemData?.street),
    country: yup.string().default(itemData?.country),
    city: yup.string().default(itemData?.city),
    zip: yup.string().default(itemData?.zip),
    phone: yup.string().default(itemData?.phone),
    type: yup.string().required('Please enter a type.').default(itemData?.type),
    zone: yup.string().required('Please enter a timezone.').default(itemData?.zone),
    currency: yup.string().required('Please enter ISO 4217 format currency.').default(itemData?.currency),
    restrictions_age: yup.number()
      .nullable(true)
      // eslint-disable-next-line eqeqeq
      .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : null))
      .default(itemData?.restrictions_age),

    default_language_code: yup.string().default(itemData?.default_language_code),
  });

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: useMemo(() => {
      return schema.default();
    }, [schema]),
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    reset(itemData);
  }, [itemData, reset]);

  const onSubmit = async (data) => {
    setLoading(true);

    const sumbitData = {
      company: auth.company?.company,
      name: data.name,
      type: data.type,
      street: data.street,
      country: data.country,
      city: data.city,
      zip: data.zip,
      phone: data.phone,
      zone: data.zone,
      currency: data.currency,
      restrictions_age: data.restrictions_age,
      default_language_code: data.default_language_code,
    };

    if (!isEdit) {
      await addNew(
        {
          collectionName: Config.DATA.COLLECTIONS.RESTAURANTS,
          data: sumbitData,
          token: auth.token,
          resourceV2: 'AdminRestaurant',
          companyIdForV2: auth.company?.company,
        },
        async (newId) => {
          getCompanyRestaurants({ token: auth.token, companyId: auth.company?.company, refresh: true });
          navigate(`/settings/restaurants/${newId}`);
          setLoading(false);
        },
        () => {
          setLoading(false);
        },
      );
    } else {
      delete sumbitData.company;

      await editById(
        {
          collectionName: Config.DATA.COLLECTIONS.RESTAURANTS,
          id: data._id,
          updateClause: { $set: sumbitData },
          token: auth.token,
          resourceV2: 'AdminRestaurant',
          updateTypeV2: 'General',
          updateDataV2: sumbitData,
          companyIdForV2: auth.company?.company,
        },
        async () => {
          getCompanyRestaurants({ token: auth.token, companyId: auth.company?.company, refresh: true });
          alertController.success({ text: 'General data updated' });
          setLoading(false);
        },
        () => {
          setLoading(false);
        },
      );
    }
  };

  const onError = (data) => console.log(data);

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)} action="#" method="POST">
      <div className="shadow sm:rounded-md sm:overflow-hidden">
        <div className="bg-white py-6 px-4 space-y-6 sm:p-6">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              General
            </h3>
          </div>

          <div className="grid grid-cols-6 gap-6">
            <TextboxWrapper
              register={register}
              name="name"
              title="Restaurant name"
              error={errors?.name}
              responsiveClassName="col-span-6 sm:col-span-3"
            />

            <DropdownWrapper
              register={register}
              name="type"
              title="Restaurant type"
              options={restaurantTypes}
              error={errors?.type}
              responsiveClassName="col-span-6 sm:col-span-3"
            />

            <TextboxWrapper
              register={register}
              name="country"
              title="Country"
              error={errors?.country}
              responsiveClassName="col-span-6 sm:col-span-3"
            />

            <TextboxWrapper
              register={register}
              name="city"
              title="City"
              error={errors?.city}
              responsiveClassName="col-span-6 sm:col-span-3"
            />

            <TextboxWrapper
              register={register}
              name="street"
              title="Street"
              error={errors?.street}
              responsiveClassName="col-span-6 sm:col-span-3"
            />

            <TextboxWrapper
              register={register}
              name="zip"
              title="Postal code"
              error={errors?.zip}
              responsiveClassName="col-span-6 sm:col-span-3"
            />

            <TextboxWrapper
              register={register}
              name="phone"
              title="Phone"
              error={errors?.phone}
              responsiveClassName="col-span-6 sm:col-span-3"
            />

            <div className="sm:col-span-3 hidden sm:block" />

            <div className="col-span-6 w-full h-[1px] bg-gray-100 mt-2 mb-2" />

            <TextboxWrapper
              register={register}
              name="currency"
              title="Currency*"
              error={errors?.currency}
              responsiveClassName="col-span-6 sm:col-span-3"
              placeholder="EUR"
            />

            <div className="col-span-6 sm:col-span-3">
              <DropDownList
                {...register('zone')}
                showLabel
                id="zones"
                label="Time zone"
                options={moment.tz
                  ?.names()
                  ?.filter((tz) => tz.includes('Europe/'))
                  ?.map((tz) => ({
                    value: tz,
                    label: tz,
                  }))}
                defaultValue={moment.tz.guess()}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              />
              {errors?.zone && (
              <p className="mt-2 text-sm text-red-600" id="email-error">
                {errors?.zone.message}
              </p>
              )}
            </div>

            <DropdownWrapper
              register={register}
              name="default_language_code"
              title="Default language"
              options={defaultLanguageCodes}
              error={errors?.default_language_code}
              responsiveClassName="col-span-6 sm:col-span-3"
            />

            <div className="col-span-6 w-full h-[1px] bg-gray-100 mt-2 mb-2" />

            <TextboxWrapper
              register={register}
              name="restrictions_age"
              title="Restrictions age"
              subtitle="Required age for restricted products"
              error={errors?.restrictions_age}
              responsiveClassName="col-span-6 sm:col-span-3"
              placeholder="18"
            />

          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
          <button
            type="button"
            className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            onClick={() => navigate('/settings/restaurants')}
          >
            Cancel
          </button>
          <FormButton text={isEdit ? 'Save' : 'Add new'} isLoading={loading} />
        </div>
      </div>
    </form>
  );
};
